import { useEffect, useState } from 'preact/hooks';
import type { SupplierDetailBody } from '../../shared/suppliers/review';
import { DateHint } from '../components/DateHint';
import { useI18n, type MessageKey } from '../i18n';
import { ApiError, apiDownload, apiGet, apiSend, apiUpload } from '../lib/api';
import { formatDate, formatDateTime } from '../lib/dates';

// Supplier review (screen 22, P2.5). Procurement approves categories, asks for information or
// rejects. The approving buyer answers for the bank details; a later change needs a new bank
// letter (D48). The broker enforces every rule; this page only shows what it offers.

type Load =
  | { kind: 'loading' }
  | { kind: 'error'; code: string; reference: string | null }
  | { kind: 'ok'; s: SupplierDetailBody };

const STATUS_TONE = {
  invited: 'info',
  submitted: 'info',
  approved: 'success',
  rejected: 'neutral',
};
const CHECK_ICON = { ok: '✓', warn: '!', fail: '✕' } as const;
const KNOWN_EVENTS = new Set([
  'INVITE_CREATE',
  'INVITE_RESEND',
  'INVITE_REOPEN',
  'INVITE_OPENED',
  'SUPPLIER_DRAFT_SAVE',
  'SUPPLIER_FILE_ADD',
  'SUPPLIER_SUBMIT',
  'SUPPLIER_APPROVE',
  'SUPPLIER_ASK_INFO',
  'SUPPLIER_REJECT',
  'BANK_VIEW',
  'BANK_CHANGE',
  'SUPPLIER_DOC_RENEW',
  'SUPPLIER_FILE_VIEW',
  'MAIL_SENT',
]);
const ERRORS = new Set(['invalid_transition', 'conflict', 'invalid_input', 'forbidden']);

function problem(err: unknown, t: (k: MessageKey) => string): string {
  const e = err instanceof ApiError ? err : null;
  const known = e && ERRORS.has(e.code) ? `sup.error.${e.code}` : 'review.error.generic';
  return t(known as MessageKey);
}

export function SupplierDetailPage({ supplierId }: { supplierId: string }) {
  const { t, lang } = useI18n();
  const [load, setLoad] = useState<Load>({ kind: 'loading' });
  const [reload, setReload] = useState(0);
  const [picked, setPicked] = useState<string[] | null>(null);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [full, setFull] = useState<{ iban: string; swift: string } | null>(null);

  useEffect(() => {
    let live = true;
    apiGet<SupplierDetailBody>(`/api/suppliers/${supplierId}`)
      .then((s) => {
        if (!live) return;
        setLoad({ kind: 'ok', s });
        setPicked(null);
      })
      .catch((err: unknown) => {
        if (!live) return;
        const e = err instanceof ApiError ? err : null;
        setLoad({ kind: 'error', code: e?.code ?? 'generic', reference: e?.correlationId ?? null });
      });
    return () => {
      live = false;
    };
  }, [supplierId, reload]);

  if (load.kind === 'loading') {
    return (
      <section class="card" aria-busy="true">
        <p>{t('req.loading')}</p>
      </section>
    );
  }
  if (load.kind === 'error') {
    return (
      <section class="card card--error" role="alert">
        <p>
          {load.code === 'not_found'
            ? t('sup.error.not_found')
            : load.code === 'forbidden'
              ? t('sup.error.forbidden')
              : t('req.loadError')}
        </p>
        {load.reference && (
          <p class="caption" dir="ltr">
            {t('error.reference', { ref: load.reference })}
          </p>
        )}
        <a class="button" href="#/suppliers">
          {t('sup.back')}
        </a>
      </section>
    );
  }

  const { s } = load;
  const categories = picked ?? s.requestedCategories;
  const catName = (id: string) => {
    const c = s.categories.find((x) => x.id === id);
    return c ? (lang === 'ar' ? c.nameAR || c.nameEN : c.nameEN) : id;
  };
  const label = (prefix: string, v: string) => (v ? t(`${prefix}.${v}` as MessageKey) : '—');

  async function run(fn: () => Promise<unknown>, done: MessageKey) {
    setBusy(true);
    setNote(null);
    setFields({});
    try {
      await fn();
      setNote({ ok: true, text: t(done) });
      setMessage('');
      setFull(null);
      setReload((r) => r + 1);
    } catch (err) {
      setNote({ ok: false, text: problem(err, t) });
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  }

  const decide = (action: 'approve' | 'askInfo' | 'reject') => {
    if (action !== 'approve' && message.trim().length < 5) {
      setNote({ ok: false, text: t('sup.decide.needMessage') });
      return;
    }
    void run(
      () =>
        apiSend('POST', `/api/suppliers/${supplierId}/action`, {
          action,
          etag: s.etag,
          ...(action === 'approve' ? { categories } : { message }),
        }),
      `sup.done.${action}` as MessageKey,
    );
  };

  const showFull = async () => {
    try {
      setFull(await apiGet(`/api/suppliers/${supplierId}/bank`));
    } catch (err) {
      setNote({ ok: false, text: problem(err, t) });
    }
  };

  const facts: [MessageKey, string, boolean?][] = [
    ['sup.f.legalName', [s.legalNameEN, s.legalNameAR].filter(Boolean).join(' · ')],
    ['reg.f.tradingName', s.tradingName || '—'],
    ['sup.f.place', [s.city, label('reg.country', s.country)].filter(Boolean).join(', ')],
    ['reg.f.crNumber', s.cr || '—', true],
    ['reg.f.crExpiry', s.crExpiry ? formatDate(s.crExpiry, lang) : t('sup.f.noExpiry')],
    ['reg.f.vatNumber', s.vat || t('sup.f.notVat'), !!s.vat],
    ['sup.f.contact', s.contact || '—'],
    ['reg.f.contactEmail', s.email || '—', true],
    [
      'reg.f.contactMobile',
      [s.mobile, s.whatsApp && `WhatsApp ${s.whatsApp}`].filter(Boolean).join(' · ') || '—',
      true,
    ],
    ['reg.f.financeEmail', s.financeEmail || '—', true],
    ['reg.f.website', s.website || '—', true],
    ['reg.f.supplyType', label('reg.supplyType', s.supplyType)],
    ['reg.f.leadTime', label('reg.leadTime', s.leadTime)],
    [
      'reg.f.deliveryAreas',
      s.deliveryAreas.map((a) => label('reg.deliveryAreas', a)).join(', ') || '—',
    ],
    [
      'reg.f.paymentTerms',
      s.paymentTerms.map((a) => label('reg.paymentTerms', a)).join(', ') || '—',
    ],
  ];

  return (
    <div class="stack">
      <p class="caption">
        <a href="#/suppliers">{t('nav.suppliers')}</a> / <span dir="ltr">{s.supplierId}</span>
      </p>
      <div class="detail-head">
        <span class={`pill pill--${STATUS_TONE[s.status]}`}>
          {t(`sup.status.${s.status}` as MessageKey)}
        </span>
        <span class="ref" dir="ltr">
          {s.supplierId}
        </span>
      </div>
      <h2 class="detail-title">{s.legalNameEN}</h2>
      <p class="caption">
        {[
          s.invitedBy && t('sup.invitedBy', { who: s.invitedBy }),
          s.submittedAt && t('sup.submittedOn', { date: formatDate(s.submittedAt, lang) }),
        ]
          .filter(Boolean)
          .join(' · ')}
      </p>
      {note && (
        <p class={note.ok ? 'form-saved' : 'form-problem'} role={note.ok ? 'status' : 'alert'}>
          {note.text}
        </p>
      )}

      <div class="pr-layout">
        <div class="stack">
          <section class="card" aria-labelledby="sup-checks">
            <h3 id="sup-checks" class="card__title">
              {t('sup.checks')}
            </h3>
            <ul class="sup-checks">
              {s.checks.map((c) => (
                <li key={c.id} class={`sup-check sup-check--${c.level}`}>
                  <span class="sup-check__icon" aria-hidden="true">
                    {CHECK_ICON[c.level]}
                  </span>
                  <span>
                    <span class="visually-hidden">{t(`sup.level.${c.level}` as MessageKey)}: </span>
                    {t(`sup.check.${c.id}.${c.detail}` as MessageKey, {
                      ...(c.values ?? {}),
                      ...(c.values?.date ? { date: formatDate(c.values.date, lang) } : {}),
                    })}
                  </span>
                </li>
              ))}
            </ul>
            {s.related === 'yes' && s.relatedDetails && (
              <p class="notice notice--action" dir="auto">
                {s.relatedDetails}
              </p>
            )}
          </section>

          <section class="card" aria-labelledby="sup-company">
            <h3 id="sup-company" class="card__title">
              {t('sup.company')}
            </h3>
            <dl class="facts">
              {facts.map(([k, v, ltr]) => (
                <div key={k}>
                  <dt>{t(k)}</dt>
                  <dd dir={ltr ? 'ltr' : 'auto'}>{v}</dd>
                </div>
              ))}
            </dl>
            {s.address && (
              <>
                <h4 class="facts__label">{t('reg.f.address')}</h4>
                <p dir="auto">{s.address}</p>
              </>
            )}
          </section>

          <section
            class={`card${s.can.decide ? ' card--emphasis' : ''}`}
            aria-labelledby="sup-cats"
          >
            <h3 id="sup-cats" class="card__title">
              {t(s.can.decide ? 'sup.cats.approve' : 'sup.cats.title')}
            </h3>
            {s.can.decide && <p class="caption">{t('sup.cats.hint')}</p>}
            <div class="reg-chips">
              {(s.status === 'approved' ? s.approvedCategories : s.requestedCategories).map((id) =>
                s.can.decide ? (
                  <button
                    key={id}
                    type="button"
                    class="chip"
                    aria-pressed={categories.includes(id)}
                    onClick={() =>
                      setPicked(
                        categories.includes(id)
                          ? categories.filter((c) => c !== id)
                          : [...categories, id],
                      )
                    }
                  >
                    {catName(id)}
                  </button>
                ) : (
                  <span key={id} class="pill pill--info">
                    {catName(id)}
                  </span>
                ),
              )}
            </div>
            {fields.categories && <p class="field__error">{t('sup.error.categories')}</p>}
            <h4 class="facts__label">{t('sup.cats.description')}</h4>
            <p dir="auto">{s.description || '—'}</p>
            {s.brands && (
              <>
                <h4 class="facts__label">{t('reg.f.brands')}</h4>
                <p dir="auto">{s.brands}</p>
              </>
            )}
          </section>

          <section class="card" aria-labelledby="sup-docs">
            <h3 id="sup-docs" class="card__title">
              {t('sup.docs')}
            </h3>
            {s.status !== 'approved' && <p class="caption">{t('sup.docs.quarantine')}</p>}
            {s.files.length === 0 ? (
              <p>—</p>
            ) : (
              <ul class="reg-docs">
                {s.files.map((f) => (
                  <li key={f.slot} class="reg-doc reg-doc--done">
                    <span class="reg-doc__label">{t(`reg.doc.${f.slot}` as MessageKey)}</span>
                    <span class="caption" dir="ltr">
                      {f.name}
                    </span>
                    <button
                      type="button"
                      class="button button--small"
                      aria-label={t('sup.docs.downloadFor', {
                        doc: t(`reg.doc.${f.slot}` as MessageKey),
                      })}
                      onClick={() =>
                        void apiDownload(
                          `/api/suppliers/${supplierId}/files/${f.slot}`,
                          f.name,
                        ).catch((err: unknown) => setNote({ ok: false, text: problem(err, t) }))
                      }
                    >
                      {t('sup.docs.download')}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {s.status === 'approved' && (
              <RenewForm
                supplierId={supplierId}
                onDone={(ok, text) => {
                  setNote({ ok, text });
                  if (ok) setReload((r) => r + 1);
                }}
              />
            )}
          </section>

          <BankCard
            s={s}
            full={full}
            busy={busy}
            onShow={() => void showFull()}
            onChange={(input, letter) =>
              void run(async () => {
                const uploaded = await apiUpload<{ name: string }>(
                  `/api/suppliers/${supplierId}/bank-letter`,
                  letter,
                );
                await apiSend('POST', `/api/suppliers/${supplierId}/bank`, {
                  ...input,
                  etag: s.etag,
                  letter: uploaded.name,
                });
              }, 'sup.done.bankChange')
            }
            fields={fields}
          />
        </div>

        <aside class="stack">
          {s.can.decide ? (
            <section class="card stack" aria-labelledby="sup-decide">
              <h3 id="sup-decide" class="card__title">
                {t('sup.decide')}
              </h3>
              <p class="caption">{t('sup.decide.hint', { n: String(categories.length) })}</p>
              {s.bank.masked && <p class="notice notice--action">{t('sup.decide.bankNote')}</p>}
              <div class="field">
                <label for="sup-message">{t('sup.decide.message')}</label>
                <textarea
                  id="sup-message"
                  rows={4}
                  dir="auto"
                  maxLength={1000}
                  value={message}
                  onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)}
                />
              </div>
              <button
                type="button"
                class="button button--primary"
                disabled={busy || categories.length === 0}
                onClick={() => decide('approve')}
              >
                {t('sup.decide.approve')}
              </button>
              <div class="actions">
                <button
                  type="button"
                  class="button"
                  disabled={busy}
                  onClick={() => decide('askInfo')}
                >
                  {t('sup.decide.askInfo')}
                </button>
                <button
                  type="button"
                  class="button button--danger"
                  disabled={busy}
                  onClick={() => decide('reject')}
                >
                  {t('sup.decide.reject')}
                </button>
              </div>
            </section>
          ) : (
            s.reviewNote && (
              <section class="card" aria-labelledby="sup-note">
                <h3 id="sup-note" class="card__title">
                  {t('sup.reviewNote')}
                </h3>
                <p dir="auto">{s.reviewNote}</p>
              </section>
            )
          )}
          <section class="card" aria-labelledby="sup-activity">
            <h3 id="sup-activity" class="card__title">
              {t('sup.activity')}
            </h3>
            {s.events.length === 0 ? (
              <p>—</p>
            ) : (
              <ol class="timeline">
                {s.events.map((e, i) => (
                  <li key={`${e.when}-${i}`}>
                    <span class="timeline__what">
                      {KNOWN_EVENTS.has(e.action)
                        ? t(`sup.event.${e.action}` as MessageKey)
                        : e.action}
                    </span>
                    <span class="caption">
                      {formatDateTime(e.when, lang)} · <span dir="ltr">{e.who}</span>
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

interface BankInput {
  bankName: string;
  accountHolder: string;
  iban: string;
  swift: string;
  reason: string;
}

function BankCard(props: {
  s: SupplierDetailBody;
  full: { iban: string; swift: string } | null;
  busy: boolean;
  fields: Record<string, string>;
  onShow: () => void;
  onChange: (input: BankInput, letter: File) => void;
}) {
  const { t, lang } = useI18n();
  const { s, full, busy, fields } = props;
  const b = s.bank;
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<BankInput>({
    bankName: '',
    accountHolder: s.legalNameEN,
    iban: '',
    swift: '',
    reason: '',
  });
  const [letter, setLetter] = useState<File | null>(null);
  const set = (k: keyof BankInput) => (e: Event) =>
    setInput({ ...input, [k]: (e.target as HTMLInputElement).value });

  return (
    <section class="card stack" aria-labelledby="sup-bank">
      <h3 id="sup-bank" class="card__title">
        {t('sup.bank')}
      </h3>
      <dl class="facts">
        <div>
          <dt>{t('reg.f.bankName')}</dt>
          <dd dir="auto">{b.bankName || '—'}</dd>
        </div>
        <div>
          <dt>{t('reg.f.accountHolder')}</dt>
          <dd dir="auto">{b.holder || '—'}</dd>
        </div>
        <div>
          <dt>{t('reg.f.iban')}</dt>
          <dd dir="ltr" class="ref">
            {full?.iban || b.masked || '—'}
          </dd>
        </div>
        <div>
          <dt>{t('reg.f.swift')}</dt>
          <dd dir="ltr">{(full ? full.swift : b.swift) || '—'}</dd>
        </div>
      </dl>
      <p>
        <span class={`pill pill--${b.confirmedBy ? 'success' : 'warning'}`}>
          {b.confirmedBy
            ? t('sup.bank.confirmedBy', { who: b.confirmedBy })
            : t('sup.bank.notConfirmed')}
        </span>
        {b.changedAt && (
          <span class="caption">
            {' '}
            {t('sup.bank.changedOn', { date: formatDate(b.changedAt, lang) })}
          </span>
        )}
      </p>
      {s.can.viewBank && !full && (
        <div class="actions">
          <button type="button" class="button button--small" onClick={props.onShow}>
            {t('sup.bank.show')}
          </button>
          <span class="caption">{t('sup.bank.showNote')}</span>
        </div>
      )}
      <p class="notice notice--info">{t('reg.bankNote')}</p>

      {s.can.changeBank &&
        (open ? (
          <form
            class="stack"
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              if (letter) props.onChange(input, letter);
            }}
          >
            <h4 class="facts__label">{t('sup.bank.changeTitle')}</h4>
            <div class="reg-grid">
              {(
                [
                  ['bankName', 'reg.f.bankName', false],
                  ['accountHolder', 'reg.f.accountHolder', false],
                  ['iban', 'reg.f.iban', true],
                  ['swift', 'reg.f.swift', true],
                ] as const
              ).map(([k, l, ltr]) => (
                <div class="field" key={k}>
                  <label for={`bank-${k}`}>{t(l)}</label>
                  <input
                    id={`bank-${k}`}
                    type="text"
                    dir={ltr ? 'ltr' : 'auto'}
                    maxLength={k === 'iban' ? 42 : 200}
                    aria-invalid={fields[k] ? true : undefined}
                    value={input[k]}
                    onInput={set(k)}
                  />
                  {fields[k] && <p class="field__error">{fields[k]}</p>}
                </div>
              ))}
            </div>
            <div class="field">
              <label for="bank-reason">{t('sup.bank.reason')}</label>
              <input
                id="bank-reason"
                type="text"
                dir="auto"
                maxLength={500}
                value={input.reason}
                onInput={set('reason')}
              />
              {fields.reason && <p class="field__error">{fields.reason}</p>}
            </div>
            <div class="field">
              <label for="bank-letter">{t('reg.doc.bankLetter')}</label>
              <input
                id="bank-letter"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setLetter((e.target as HTMLInputElement).files?.[0] ?? null)}
              />
              {(fields.letter || fields.file) && (
                <p class="field__error">{fields.letter ?? fields.file}</p>
              )}
            </div>
            <p class="caption">{t('sup.bank.changeNote')}</p>
            <div class="actions">
              <button type="submit" class="button button--primary" disabled={busy || !letter}>
                {t('sup.bank.changeSubmit')}
              </button>
              <button type="button" class="button" onClick={() => setOpen(false)}>
                {t('sup.bank.changeClose')}
              </button>
            </div>
          </form>
        ) : (
          <div class="actions">
            <button type="button" class="button" onClick={() => setOpen(true)}>
              {t('sup.bank.change')}
            </button>
          </div>
        ))}
    </section>
  );
}

/** Procurement files a renewed CR or VAT certificate with its new expiry date (P2.8). */
function RenewForm(props: { supplierId: string; onDone: (ok: boolean, text: string) => void }) {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);
  const [doc, setDoc] = useState<'cr' | 'vat'>('cr');
  const [expires, setExpires] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [fields, setFields] = useState<Record<string, string>>({});

  if (!open) {
    return (
      <div class="actions">
        <button type="button" class="button button--small" onClick={() => setOpen(true)}>
          {t('sup.renew.open')}
        </button>
      </div>
    );
  }
  return (
    <form
      class="stack"
      noValidate
      onSubmit={(e) => {
        e.preventDefault();
        if (!file) return;
        setBusy(true);
        setFields({});
        apiUpload(
          `/api/suppliers/${props.supplierId}/renew?doc=${doc}&expires=${encodeURIComponent(expires)}`,
          file,
        )
          .then(() => {
            setOpen(false);
            props.onDone(true, t('sup.renew.done'));
          })
          .catch((err: unknown) => {
            const e = err instanceof ApiError ? err : null;
            setFields(e?.fields ?? {});
            props.onDone(false, problem(err, t));
          })
          .finally(() => setBusy(false));
      }}
    >
      <h4 class="facts__label">{t('sup.renew.title')}</h4>
      <div class="reg-grid">
        <div class="field">
          <label for="renew-doc">{t('sup.renew.doc')}</label>
          <select
            id="renew-doc"
            value={doc}
            onChange={(e) => setDoc((e.target as HTMLSelectElement).value as 'cr' | 'vat')}
          >
            <option value="cr">{t('reg.doc.cr')}</option>
            <option value="vat">{t('reg.doc.vat')}</option>
          </select>
        </div>
        <div class="field">
          <label for="renew-expires">{t('sup.renew.expires')}</label>
          <input
            id="renew-expires"
            type="date"
            dir="ltr"
            value={expires}
            aria-invalid={fields.expires ? true : undefined}
            aria-describedby="renew-expires-when"
            onInput={(e) => setExpires((e.target as HTMLInputElement).value)}
          />
          <DateHint id="renew-expires-when" value={expires} />
          {fields.expires && <p class="field__error">{fields.expires}</p>}
        </div>
      </div>
      <div class="field">
        <label for="renew-file">{t('sup.renew.file')}</label>
        <input
          id="renew-file"
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          onChange={(e) => setFile((e.target as HTMLInputElement).files?.[0] ?? null)}
        />
        {fields.file && <p class="field__error">{fields.file}</p>}
      </div>
      <div class="actions">
        <button type="submit" class="button button--primary" disabled={busy || !file || !expires}>
          {t('sup.renew.save')}
        </button>
        <button type="button" class="button" onClick={() => setOpen(false)}>
          {t('sup.bank.changeClose')}
        </button>
      </div>
    </form>
  );
}
